import Service from '@ember/service';
import { setComponentTemplate } from '@ember/component';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import { A } from '@ember/array';
import MathUtils from 'mewe/utils/math-utils';
import { next } from '@ember/runloop';

export default class DynamicPopupsService extends Service {
  @tracked components = A();
  @tracked _registered = A();

  async openPopup(popupName, args = {}, ctx = null) {
    // in case of some routes outside the app, wormhole is not created in app.js route
    this.setupPopupWormhole();

    if (!args.allowMultipleInstances) {
      const existingPopup = this._registered.find((el) => el.popupName === popupName);
      if (existingPopup) {
        this.close(existingPopup.popupId);
      }
    }

    // register dialog even before importing/injecting it to
    // prevent multiple instances when it's needed. Assign unique id to dialog
    // to be able to filter proper dialog to close
    args.popupId = MathUtils.generateId();
    this.register({ popupName, popupId: args.popupId });

    const owner = ctx ? getOwner(ctx) : getOwner(this);
    const factory = owner.factoryFor(`component:${popupName}`);
    if (!factory) {
      let [template, module] = await Promise.all([
        import(`../pods/components/dropdowns/${popupName}/template.hbs`),
        import(`../pods/components/dropdowns/${popupName}`),
      ]);
      setComponentTemplate(template.default, module.default);
      owner.register(`component:${popupName}`, module.default);
    }

    // postponed in order to firstly close existing popup if that's the case,
    // otherwise there might be strange behaviour (MW-3510)
    next(() => {
      this.components.pushObject({
        name: popupName,
        popupName: popupName,
        popupArgs: args,
        args,
      });
    });
    return owner.factoryFor(`component:${popupName}`);
  }

  setupPopupWormhole() {
    if (!document.getElementById('mw-wormhole-popup')) {
      const popupWormhole = document.createElement('div');
      popupWormhole.id = 'mw-wormhole-popup';
      document.body.append(popupWormhole);
    }
  }

  closeAll() {
    this.components.forEach((comp) => {
      this.close(comp.popupArgs.popupId);
    });
  }

  closeByPopupName(popupName) {
    const comp = this.components.find((comp) => comp.popupName === popupName);
    if (comp) {
      this.close(comp.popupArgs.popupId);
    }
  }

  close(popupId) {
    if (!popupId) return;

    const comp = this.getPopupById(popupId);

    if (comp) {
      comp.popupArgs.onClose?.();

      // filter out the dialog from the registered list
      this.unregister(popupId);
      this.components.removeObject(comp);
    }
  }

  register(registerData) {
    const found = this._registered.find((el) => el.popupName === registerData.popupName);
    if (!found) this._registered.pushObject(registerData);
  }

  unregister(popupId) {
    this._registered = A(this._registered.filter((el) => el.popupId !== popupId));
  }

  getPopupById(popupId) {
    return this.components.find((comp) => comp.popupArgs.popupId === popupId);
  }
}
